import {
  computed, ref, onMounted, onUnmounted,
} from 'vue';
import { useStore } from 'vuex';
// eslint-disable-next-line import-x/extensions
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import fallbackAvatar from '../assets/logos/refrag-avatar.svg';
import { playSound, stopSound } from '../utils/matchmakingSounds';

// The match-found accept flow, shared by the classic LobbyFoundModal and the v5 one.
export default function useLobbyFound(props) {
  const { t } = useI18n();
  const store = useStore();
  const auth = useAuth();
  const accepting = ref(false);
  const acceptedLocally = ref(false);
  const error = ref(null);
  const secondsLeft = ref(30);
  let timer = null;

  const teams = computed(() => (props.lobby.teams || [props.lobby.team1, props.lobby.team2])
    .filter((team) => team?.length));
  const members = computed(() => teams.value.flat());
  const teamFormat = computed(() => (teams.value.length > 1 ? teams.value.map((team) => team.length).join('v') : null));
  const modeArt = computed(() => {
    const name = props.lobby.play_mode?.name;
    return name ? `/img/mods/${name.toLowerCase()}.webp` : null;
  });
  const modeArtFailed = ref(false);
  const mapPinFailed = ref(false);
  const acceptedCount = computed(() => members.value.filter((m) => m.accepted).length);
  const allIn = computed(
    () => members.value.length > 0 && acceptedCount.value === members.value.length,
  );
  const accepted = computed(() => {
    const me = members.value.find((m) => m.user?.uuid === auth.user()?.uuid);
    return acceptedLocally.value || !!me?.accepted;
  });

  const remainingLabel = computed(() => {
    const m = Math.floor(secondsLeft.value / 60);
    const s = String(secondsLeft.value % 60).padStart(2, '0');
    return `${m}:${s}`;
  });
  const urgentTimer = computed(() => secondsLeft.value <= 10);

  function memberName(member) {
    return member?.user?.username || member?.user?.personaname || '';
  }

  function avatarOf(member) {
    return member?.user?.avatarfull || member?.user?.avatar || fallbackAvatar;
  }

  function tickCountdown() {
    if (props.lobby.accept_deadline_at) {
      const ms = new Date(props.lobby.accept_deadline_at) - Date.now();
      secondsLeft.value = Math.max(0, Math.round(ms / 1000));
    } else {
      secondsLeft.value = Math.max(0, secondsLeft.value - 1);
    }
  }

  function accept() {
    accepting.value = true;
    error.value = null;
    axios
      .post(`/matchmaking/lobbies/${props.lobby.uuid}/accept`)
      .then(({ data }) => {
        acceptedLocally.value = true;
        stopSound('match-found', 0.15);
        stopSound('countdown', 0.15);
        playSound('accepted');
        store.commit('setMatchmakingLobby', data);
      })
      .catch((err) => {
        error.value = err.response?.data?.error || t('play.matchmaking.accept_failed');
      })
      .finally(() => {
        accepting.value = false;
      });
  }

  onMounted(() => {
    tickCountdown();
    timer = setInterval(tickCountdown, 1000);
  });
  onUnmounted(() => clearInterval(timer));
  const cancelling = ref(false);

  // Only the managers of the LAN party this lobby belongs to; elsewhere they are viewers.
  const lanManager = computed(() => {
    const lan = props.lobby.lan_party;
    const mine = store.getters.matchmakingContext?.creator?.slug;
    return !!lan && !!mine && mine === lan.creator;
  });

  function cancelMatch() {
    cancelling.value = true;
    error.value = null;
    axios.delete(`/matchmaking/lan_parties/${props.lobby.lan_party.uuid}/lobbies/${props.lobby.uuid}`)
      .catch((e) => { error.value = e.response?.data?.error || t('play.lan_party.network_error'); })
      .finally(() => { cancelling.value = false; });
  }

  return {
    accepting,
    error,
    members,
    teamFormat,
    modeArt,
    modeArtFailed,
    mapPinFailed,
    acceptedCount,
    allIn,
    accepted,
    remainingLabel,
    urgentTimer,
    memberName,
    avatarOf,
    accept,
    cancelling,
    lanManager,
    cancelMatch,
  };
}
